import * as React from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/shared/lib/utils";
import { Button } from "@/shared/ui/button";
import { Calendar } from "@/shared/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/ui/popover";
import { Input } from "@/shared/ui/input";

interface DateTimePickerProps {
  date: Date | undefined;
  setDate: (date: Date | undefined) => void;
  disabled?: boolean;
  className?: string;
}

export function DateTimePicker({
  date,
  setDate,
  disabled,
  className,
}: DateTimePickerProps) {
  const [isOpen, setIsOpen] = React.useState(false);

  const hours = date ? date.getHours() : 0;
  const minutes = date ? date.getMinutes() : 0;

  const handleDateSelect = (selectedDate: Date | undefined) => {
    if (!selectedDate) return;
    
    const newDate = new Date(selectedDate);
    if (date) {
      newDate.setHours(date.getHours());
      newDate.setMinutes(date.getMinutes());
    }
    setDate(newDate);
  };

  const handleTimeChange = (type: "hours" | "minutes", value: string) => {
    if (!date) return;
    
    const newDate = new Date(date);
    const numValue = parseInt(value, 10);
    
    if (isNaN(numValue)) return;
    
    if (type === "hours" && numValue >= 0 && numValue <= 23) {
      newDate.setHours(numValue);
      setDate(newDate);
    } else if (type === "minutes" && numValue >= 0 && numValue <= 59) {
      newDate.setMinutes(numValue);
      setDate(newDate);
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal",
            !date && "text-muted-foreground",
            className
          )}
          disabled={disabled}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {date ? format(date, "PPP HH:mm") : <span>Pick a date</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="p-3 space-y-3">
          <Calendar
            mode="single"
            selected={date}
            onSelect={handleDateSelect}
            initialFocus
          />
          <div className="flex items-center gap-2 px-3">
            <div className="flex-1">
              <label className="text-xs text-muted-foreground mb-1 block">
                Hours
              </label>
              <Input
                type="number"
                min="0"
                max="23"
                value={hours.toString().padStart(2, "0")}
                onChange={(e) => handleTimeChange("hours", e.target.value)}
                className="text-center"
              />
            </div>
            <div className="text-2xl font-bold pt-5">:</div>
            <div className="flex-1">
              <label className="text-xs text-muted-foreground mb-1 block">
                Minutes
              </label>
              <Input
                type="number"
                min="0"
                max="59"
                value={minutes.toString().padStart(2, "0")}
                onChange={(e) => handleTimeChange("minutes", e.target.value)}
                className="text-center"
              />
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
